import { BitkitRibbon, BitkitRibbonProps } from '@bitrise/bitkit-v2';
import { ReactNode, useState } from 'react';

import useMonolithApiMutation from '@/hooks/api/useMonolithApiMutation';
import useMonolithApiQuery from '@/hooks/api/useMonolithApiQuery';

type Notification = {
  id: string;
  name: string;
};

type NotificationsResponse = {
  notifications: Notification[];
};

type BannerConfig = {
  isDismissible: boolean;
  ribbonColor: BitkitRibbonProps['colorVariant'];
  title: string;
  content: ReactNode;
};

const NotificationContainer = ({
  notificationId,
  isDismissible,
  children,
  ribbonColor,
  title,
}: {
  notificationId: string;
  isDismissible: boolean;
  children: ReactNode;
  ribbonColor: BitkitRibbonProps['colorVariant'];
  title: string;
}) => {
  const [isClosed, setIsClosed] = useState(false);
  const { mutate: dismissBanner } = useMonolithApiMutation<unknown, unknown, { isDismissed: boolean }>(
    `/notifications/${notificationId}`,
    'PATCH',
  );

  if (isClosed) {
    return null;
  }

  const onClose = () => {
    setIsClosed(true);
    dismissBanner({ isDismissed: true });
  };

  return (
    <BitkitRibbon
      data-testid="notification-ribbon"
      colorVariant={ribbonColor}
      onDismiss={isDismissible ? onClose : undefined}
      title={title}
    >
      {children}
    </BitkitRibbon>
  );
};

export const KNOWN_BANNERS: Record<string, BannerConfig> = {
  'platform-consumption-p75-banner': {
    isDismissible: true,
    ribbonColor: 'yellow',
    title: '75% of infrastructure utilization limit reached.',
    content: 'Builds will not start upon reaching the limit.',
  },
  'platform-consumption-p85-banner': {
    isDismissible: true,
    ribbonColor: 'yellow',
    title: '85% of infrastructure utilization limit reached.',
    content: 'Builds will not start upon reaching the limit.',
  },
  'platform-consumption-p100-banner': {
    isDismissible: false,
    ribbonColor: 'yellow',
    title: 'Infrastructure utilization limit reached.',
    content: 'Builds are suspended until the billing cycle resets.',
  },
};

const DynamicBanners = () => {
  const {
    data: { notifications = [] } = {},
    isLoading,
    isError,
  } = useMonolithApiQuery<NotificationsResponse, unknown>(['notifications'], '/notifications');

  if (isLoading || isError) {
    return null;
  }

  return Object.entries(KNOWN_BANNERS).map(([key, banner]) => {
    const notification = notifications.find(({ name }) => key === name);
    if (!notification) {
      return null;
    }

    return (
      <NotificationContainer
        key={notification.id}
        notificationId={notification.id}
        isDismissible={banner.isDismissible}
        ribbonColor={banner.ribbonColor}
        title={banner.title}
      >
        {banner.content}
      </NotificationContainer>
    );
  });
};

export default DynamicBanners;
